import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";

// Modals render into document.body. Inside the page they inherited the page's entrance transform, which
// made `position: fixed` relative to the page instead of the viewport (the source of the offset,
// clipped and overlapping dialogs). Open modals are counted so nested dialogs keep the page scroll locked.
let openModals = 0;
const lockScroll = () => {
  openModals += 1;
  if (openModals === 1) {
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.body.dataset.leadsScrollLock = document.body.style.overflow || "";
    document.body.style.overflow = "hidden";
    if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
  }
};
const unlockScroll = () => {
  openModals = Math.max(0, openModals - 1);
  if (openModals === 0) {
    document.body.style.overflow = document.body.dataset.leadsScrollLock || "";
    document.body.style.paddingRight = "";
    delete document.body.dataset.leadsScrollLock;
  }
};
const focusable = "a[href], button:not([disabled]), input:not([disabled]):not([type='hidden']), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

const LeadModal = ({ title, eyebrow, description, onClose, size = "md", busy = false, footer, children, as = "div", onSubmit, className = "", initialFocus = true }) => {
  const titleId = useId();
  const panelRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = busy ? null : onClose;

  useEffect(() => {
    const previous = document.activeElement;
    lockScroll();
    const panel = panelRef.current;
    if (initialFocus && panel) {
      const target = panel.querySelector("[data-autofocus]") || panel.querySelector(".leads-modal-body input:not([type='hidden']), .leads-modal-body select, .leads-modal-body textarea") || panel;
      window.requestAnimationFrame(() => target.focus({ preventScroll: true }));
    }
    const onKey = (event) => {
      if (event.key === "Escape" && closeRef.current) { event.stopPropagation(); closeRef.current(); }
      if (event.key === "Tab" && panelRef.current) {
        const items = [...panelRef.current.querySelectorAll(focusable)].filter((item) => item.offsetParent !== null);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      unlockScroll();
      if (previous && typeof previous.focus === "function") previous.focus({ preventScroll: true });
    };
  }, [initialFocus]);

  const Body = as === "form" ? "form" : "div";
  return createPortal(
    <div className="leads-modal-root" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && closeRef.current) closeRef.current(); }}>
      <section ref={panelRef} className={`leads-modal is-${size} ${className}`} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <Body className="leads-modal-frame" onSubmit={onSubmit} noValidate={as === "form" ? true : undefined}>
          <header className="leads-modal-header">
            <div>
              {eyebrow && <p className="leads-eyebrow">{eyebrow}</p>}
              <h2 id={titleId}>{title}</h2>
              {description && <p className="leads-modal-description">{description}</p>}
            </div>
            <button type="button" className="leads-icon-button" onClick={() => closeRef.current?.()} aria-label="Close" disabled={busy}><i className="ti ti-x" /></button>
          </header>
          <div className="leads-modal-body">{children}</div>
          {footer && <footer className="leads-modal-footer">{footer}</footer>}
        </Body>
      </section>
    </div>,
    document.body
  );
};

export const ConfirmDialog = ({ title, body, confirmLabel = "Delete", tone = "danger", busy = false, onConfirm, onCancel }) => (
  <LeadModal title={title} eyebrow="Please confirm" size="sm" busy={busy} onClose={onCancel} footer={<>
    <button type="button" className="leads-secondary-button" onClick={onCancel} disabled={busy} data-autofocus={tone === "danger" ? true : undefined}>Cancel</button>
    <button type="button" className={tone === "danger" ? "leads-danger-button" : "leads-primary-button"} onClick={onConfirm} disabled={busy} data-autofocus={tone === "danger" ? undefined : true}>{busy ? <><i className="ti ti-loader-2 leads-spin" />Working…</> : confirmLabel}</button>
  </>}>
    <div className={`leads-confirm-body is-${tone}`}><span><i className={`ti ${tone === "danger" ? "ti-alert-triangle" : "ti-info-circle"}`} /></span><p>{body}</p></div>
  </LeadModal>
);

export default LeadModal;
